import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Badge,
  Button,
  Accordion,
  Spinner
} from 'react-bootstrap';
import { 
  FaCalendarAlt,
  FaUniversity,
  FaGraduationCap,
  FaArrowRight,
  FaSearch,
  FaFilter,
  FaFileAlt,
  FaDownload,
  FaHeart,
  FaShareAlt,
  FaInfoCircle,
  FaCalendarCheck,
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronUp,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileImage,
  FaFile
} from 'react-icons/fa';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';
import axios from 'axios';

const CalendarOfEvents = () => {
  const [events, setEvents] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedYear, setExpandedYear] = useState(null);
  const [favorites, setFavorites] = useState([]);

  const API_URL = `${BASE_URL}/calendar_of_events.php`;

  const getImageUrl = useCallback((path) => {
    if (!path) return null;
    if (path.startsWith('/')) {
      return `${UPLOAD_BASE_URL}${path}`;
    }
    return `${UPLOAD_BASE_URL}/${path}`;
  }, []);

  const getEventIcon = useCallback((eventType) => {
    return eventType === 'university' ? <FaUniversity /> : <FaGraduationCap />;
  }, []);

  const getEventColor = useCallback((eventType) => {
    return eventType === 'university' ? '#1976d2' : '#4caf50';
  }, []);

  const getEventBgColor = useCallback((eventType) => {
    return eventType === 'university' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(255, 255, 255, 0.1)';
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  }, []);

  const getFileIcon = (filePath) => {
    if (!filePath) return FaFile;
    
    const extension = filePath.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return FaFilePdf;
      case 'doc':
      case 'docx':
        return FaFileWord;
      case 'xls':
      case 'xlsx':
        return FaFileExcel;
      case 'ppt':
      case 'pptx':
        return FaFilePowerpoint;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return FaFileImage;
      default:
        return FaFile;
    }
  };

  const getFileIconColor = (filePath) => {
    if (!filePath) return '#6c757d';
    
    const extension = filePath.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return '#dc3545';
      case 'doc':
      case 'docx':
        return '#0d6efd';
      case 'xls':
      case 'xlsx':
        return '#198754';
      case 'ppt':
      case 'pptx':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  const isFavorite = useCallback((eventId) => {
    return favorites.includes(eventId);
  }, [favorites]);

  const toggleFavorite = useCallback((eventId) => {
    setFavorites(prev => 
      prev.includes(eventId) 
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  }, []);

  const extractAcademicYears = useCallback((eventsData) => {
    if (!Array.isArray(eventsData)) return [];
    
    const yearsMap = new Map();
    eventsData.forEach(event => {
      if (event.academic_year) {
        yearsMap.set(event.academic_year_id, {
          id: event.academic_year_id,
          year_name: event.academic_year,
          events: []
        });
      }
    });
    
    return Array.from(yearsMap.values());
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.get(API_URL);
      const result = response.data;

      if (result.success && Array.isArray(result.data) && result.data.length > 0) {
        // Extract academic years
        const years = extractAcademicYears(result.data);
        
        // Group events by academic year
        const yearsWithEvents = years.map(year => ({
          ...year,
          events: result.data.filter(event => 
            event.academic_year_id == year.id
          ).map(event => ({
            ...event,
            id: parseInt(event.id),
            formattedDate: formatDate(event.created_at),
            color: getEventColor(event.event_type),
            bgColor: getEventBgColor(event.event_type),
            hasDocument: !!event.document_file,
            fileIcon: getFileIcon(event.document_file),
            fileIconColor: getFileIconColor(event.document_file)
          }))
        }));

        // Sort years in descending order (newest first)
        yearsWithEvents.sort((a, b) => b.year_name.localeCompare(a.year_name));
        
        setAcademicYears(yearsWithEvents);
        
        // Set first year as expanded by default
        if (yearsWithEvents.length > 0) {
          setExpandedYear(yearsWithEvents[0].id);
        }
      } else {
        setError('No events available');
        setAcademicYears([]);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
      setAcademicYears([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL, extractAcademicYears, formatDate, getEventColor, getEventBgColor]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleDownloadDocument = (event) => {
    if (event.document_file) {
      const link = document.createElement('a');
      link.href = getImageUrl(event.document_file);
      link.download = event.document_file.split('/').pop() || 'event-document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleShare = (event) => {
    if (navigator.share) {
      navigator.share({
        title: event.title,
        text: event.description || 'Check out this event!',
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Event link copied to clipboard!');
    }
  };

  const toggleYearAccordion = (yearId) => {
    setExpandedYear(expandedYear === yearId ? null : yearId);
  };

  const filteredAcademicYears = academicYears.map(year => ({
    ...year,
    events: year.events.filter(event => {
      if (searchTerm) {
        const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase()));
        if (!matchesSearch) return false;
      }
      
      if (activeFilter !== 'all') {
        switch (activeFilter) {
          case 'university':
            return event.event_type === 'university';
          case 'college':
            return event.event_type === 'college';
          case 'with-documents':
            return event.hasDocument;
          default:
            return true;
        }
      }
      return true;
    })
  })).filter(year => year.events.length > 0);

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <h4 className="mt-4" style={{ color: '#1a237e' }}>Loading Campus Events</h4>
          <p className="text-muted">Discovering exciting upcoming activities...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="border-0 shadow-sm">
          <div className="d-flex align-items-center">
            <FaExclamationTriangle className="me-3" size={24} />
            <div>
              <Alert.Heading>Unable to Load Events</Alert.Heading>
              <p>{error}</p>
              <Button variant="primary" onClick={fetchEvents}>
                Try Again
              </Button>
            </div>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #283593 0%, #1a237e 100%)',
        padding: '60px 0',
        color: 'white'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <Badge bg="light" text="dark" className="mb-3 px-3 py-2">
                <FaCalendarAlt className="me-2" /> Events Calendar
              </Badge>
              <h1 className="display-5 fw-bold mb-3">
                Academic Calender of Events
              </h1>
              <p className="lead mb-4">
                Browse events organized by academic year. Each event includes full details, 
                documents, and important information.
              </p>
              <div className="d-flex flex-wrap gap-4">
                <div>
                  <h3 className="mb-0">{academicYears.reduce((sum, year) => sum + year.events.length, 0)}</h3>
                  <small className="text-white-50">Total Events</small>
                </div>
                <div>
                  <h3 className="mb-0">{academicYears.length}</h3>
                  <small className="text-white-50">Academic Years</small>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Search and Filter Section */}
      <Container className="mt-4">
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4">
            <Row className="align-items-center">
              <Col md={6}>
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0">
                    <FaSearch />
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search events by title or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex flex-wrap gap-2 justify-content-md-end mt-3 mt-md-0">
                  {/* Filter Tabs */}
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      className={`btn ${activeFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                      onClick={() => setActiveFilter('all')}
                    >
                      All Events
                    </button>
                    <button
                      className={`btn ${activeFilter === 'university' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                      onClick={() => setActiveFilter('university')}
                    >
                      <FaUniversity className="me-1" />
                      University
                    </button>
                    <button
                      className={`btn ${activeFilter === 'college' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                      onClick={() => setActiveFilter('college')}
                    >
                      <FaGraduationCap className="me-1" />
                      College
                    </button>
                    <button
                      className={`btn ${activeFilter === 'with-documents' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                      onClick={() => setActiveFilter('with-documents')}
                    >
                      <FaFileAlt className="me-1" />
                      With Documents
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>

      {/* Academic Year Events Section */}
      <Container className="py-5">
        {filteredAcademicYears.length > 0 ? (
          <Accordion activeKey={expandedYear} onSelect={(key) => setExpandedYear(key)}>
            {filteredAcademicYears.map((year) => {
              const totalEvents = year.events.length;
              const universityEvents = year.events.filter(e => e.event_type === 'university').length;
              const collegeEvents = year.events.filter(e => e.event_type === 'college').length;
              const documentEvents = year.events.filter(e => e.hasDocument).length;

              return (
                <Accordion.Item key={year.id} eventKey={year.id}>
                  <Accordion.Header>
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          <FaCalendarCheck size={24} style={{ color: '#1a237e' }} />
                        </div>
                        <div>
                          <h5 className="mb-0">{year.year_name}</h5>
                          <div className="d-flex gap-3 mt-1">
                            <Badge bg="info" className="px-2">
                              {totalEvents} Events
                            </Badge>
                            <Badge bg="primary" className="px-2">
                              {universityEvents} University
                            </Badge>
                            <Badge bg="success" className="px-2">
                              {collegeEvents} College
                            </Badge>
                            {documentEvents > 0 && (
                              <Badge bg="warning" className="px-2">
                                {documentEvents} Documents
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="me-3">
                        {expandedYear === year.id ? <FaChevronUp /> : <FaChevronDown />}
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body className="p-0">
                    <div className="events-list p-4">
                      {year.events.map((event) => (
                        <Card key={event.id} className="mb-4 border-0 shadow-sm">
                          <Card.Body className="p-4">
                            <Row>
                              <Col lg={8}>
                                {/* Event Header */}
                                <div className="d-flex align-items-start mb-3">
                                  <div className="me-3">
                                    <Badge 
                                      style={{ 
                                        background: event.bgColor, 
                                        color: event.color,
                                        padding: '8px 16px',
                                        fontSize: '0.9rem'
                                      }}
                                    >
                                      {getEventIcon(event.event_type)}
                                      <span className="ms-2">
                                        {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)} Event
                                      </span>
                                    </Badge>
                                  </div>
                                  <div>
                                    <small className="text-muted">
                                      <FaCalendarAlt className="me-1" />
                                      Published: {event.formattedDate}
                                    </small>
                                  </div>
                                </div>

                                {/* Event Title */}
                                <h4 style={{ color: '#1a237e', marginBottom: '20px' }}>
                                  {event.title}
                                </h4>

                                {/* Event Description - Full Details */}
                                <div className="mb-4">
                                  <h6 className="mb-3">
                                    <FaInfoCircle className="me-2" />
                                    Event Details
                                  </h6>
                                  <div className="p-3 bg-light rounded">
                                    {event.description ? (
                                      <div dangerouslySetInnerHTML={{ 
                                        __html: event.description.replace(/\n/g, '<br/>') 
                                      }} />
                                    ) : (
                                      <p className="text-muted mb-0">No detailed description available.</p>
                                    )}
                                  </div>
                                </div>

                                {/* Document Section */}
                                {event.hasDocument && (
                                  <div className="mb-4">
                                    <h6 className="mb-3">
                                      <FaFileAlt className="me-2" />
                                      Event Documents
                                    </h6>
                                    <Card className="border">
                                      <Card.Body className="p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                          <div className="d-flex align-items-center">
                                            <div className="me-3">
                                              <event.fileIcon size={32} style={{ color: event.fileIconColor }} />
                                            </div>
                                            <div>
                                              <h6 className="mb-1">
                                                {event.document_file.split('/').pop()}
                                              </h6>
                                              <small className="text-muted">
                                                Click download to get the event document
                                              </small>
                                            </div>
                                          </div>
                                          <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleDownloadDocument(event)}
                                          >
                                            <FaDownload className="me-2" />
                                            Download
                                          </Button>
                                        </div>
                                      </Card.Body>
                                    </Card>
                                  </div>
                                )}

                                {/* Action Buttons */}
                                <div className="d-flex gap-3 mt-4">
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => toggleFavorite(event.id)}
                                  >
                                    <FaHeart className={isFavorite(event.id) ? 'text-danger' : ''} />
                                    <span className="ms-2">
                                      {isFavorite(event.id) ? 'Saved' : 'Save to Favorites'}
                                    </span>
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleShare(event)}
                                  >
                                    <FaShareAlt className="me-2" />
                                    Share Event
                                  </Button>
                                </div>
                              </Col>
                              
                              <Col lg={4}>
                                {/* Event Metadata Sidebar */}
                                <Card className="border-0 bg-light">
                                  <Card.Body className="p-3">
                                    <h6 className="mb-3">Event Information</h6>
                                    
                                    <div className="mb-3">
                                      <small className="text-muted d-block">Event Type</small>
                                      <Badge 
                                        style={{ 
                                          background: event.bgColor, 
                                          color: event.color
                                        }}
                                        className="px-3 py-2"
                                      >
                                        {event.event_type.toUpperCase()}
                                      </Badge>
                                    </div>
                                    
                                    <div className="mb-3">
                                      <small className="text-muted d-block">Academic Year</small>
                                      <div className="fw-semibold">{year.year_name}</div>
                                    </div>
                                    
                                    <div className="mb-3">
                                      <small className="text-muted d-block">Published Date</small>
                                      <div className="fw-semibold">{event.formattedDate}</div>
                                    </div>
                                    
                                    <div className="mb-3">
                                      <small className="text-muted d-block">Status</small>
                                      <Badge bg="success" className="px-3 py-2">
                                        Published
                                      </Badge>
                                    </div>
                                    
                                    {event.hasDocument && (
                                      <div>
                                        <small className="text-muted d-block mb-2">Document Available</small>
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          className="w-100"
                                          onClick={() => handleDownloadDocument(event)}
                                        >
                                          <FaDownload className="me-2" />
                                          Download Document
                                        </Button>
                                      </div>
                                    )}
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5">
              <FaCalendarAlt size={48} className="text-muted mb-3" />
              <h4 className="mb-3">No Events Found</h4>
              <p className="text-muted mb-4">
                {searchTerm 
                  ? `No events match "${searchTerm}" for the selected filters`
                  : "No events are currently available"}
              </p>
              <Button 
                variant="outline-primary"
                onClick={() => {
                  setSearchTerm('');
                  setActiveFilter('all');
                }}
              >
                Show All Events
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Academic Year Summary */}
        {academicYears.length > 0 && (
          <Card className="border-0 shadow-sm mt-5">
            <Card.Body>
              <h5 className="mb-4">
                <FaCalendarCheck className="me-2" />
                Calender of Events Summary by Academic Year
              </h5>
              <Row>
                {academicYears.map(year => {
                  const totalEvents = year.events.length;
                  const universityEvents = year.events.filter(e => e.event_type === 'university').length;
                  const collegeEvents = year.events.filter(e => e.event_type === 'college').length;
                  const documentEvents = year.events.filter(e => e.hasDocument).length;

                  return (
                    <Col key={year.id} lg={3} md={6} className="mb-3">
                      <Card className="border h-100">
                        <Card.Body className="text-center">
                          <h5 className="mb-3">{year.year_name}</h5>
                          <div className="d-flex justify-content-around mb-3">
                            <div>
                              <h4 className="mb-0">{totalEvents}</h4>
                              <small className="text-muted">Total</small>
                            </div>
                            <div>
                              <h4 className="mb-0" style={{ color: '#1976d2' }}>{universityEvents}</h4>
                              <small className="text-muted">University</small>
                            </div>
                            <div>
                              <h4 className="mb-0" style={{ color: '#f7f7f7ff' }}>{collegeEvents}</h4>
                              <small className="text-muted">College</small>
                            </div>
                          </div>
                          {documentEvents > 0 && (
                            <Badge bg="warning" className="px-3 py-2">
                              {documentEvents} Documents Available
                            </Badge>
                          )}
                        </Card.Body>
                        <Card.Footer className="bg-transparent border-0 text-center">
                          <Button
                            variant="link"
                            onClick={() => toggleYearAccordion(year.id)}
                          >
                            {expandedYear === year.id ? 'Collapse' : 'View Events'} 
                            {expandedYear === year.id ? <FaChevronUp className="ms-2" /> : <FaChevronDown className="ms-2" />}
                          </Button>
                        </Card.Footer>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default CalendarOfEvents;